import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import usePageTitle from '../hooks/usePageTitle';
import {
  Typography,
  Box,
  Alert,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Paper,
  Skeleton,
  TextField,
  Button,
  CircularProgress,
  Chip,
  Divider,
} from '@mui/material';
import SpeedIcon from '@mui/icons-material/Speed';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import Layout from '../components/Layout';
import authService from '../services/authService';
import adminService from '../services/adminService';
import { useUnion } from '../contexts/UnionContext';
import { useApartment } from '../contexts/ApartmentContext';
import axios from 'axios';

const THIS_YEAR = new Date().getFullYear();
const THIS_MONTH = new Date().getMonth() + 1;
const MONTH_NAMES = [
  '', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function Dashboard() {
  const { t } = useTranslation();
  usePageTitle(t('dashboard.title'));
  const { activeUnion, isUnionAdmin } = useUnion();
  const { activeApartment } = useApartment();
  const isAdminView = isUnionAdmin && !activeApartment;
  const [userInfo, setUserInfo] = useState(null);
  const [error, setError] = useState('');

  // Admin summary state
  const [adminStats, setAdminStats] = useState(null);
  const [adminStatsLoading, setAdminStatsLoading] = useState(false);

  // Consumption charts state
  const [chartTypes, setChartTypes] = useState([]);   // BY_CONSUMPTION utility types
  const [chartData, setChartData] = useState({});     // { utId: [{label, value}] }
  const [chartsLoading, setChartsLoading] = useState(false);

  // Meter readings state
  const [consumptionData, setConsumptionData] = useState([]);
  const [loadingConsumption, setLoadingConsumption] = useState(false);
  const [values, setValues] = useState({});       // { `${apt_id}-${ut_id}`: string }
  const [saving, setSaving] = useState({});       // { `${apt_id}-${ut_id}`: bool }
  const [saved, setSaved] = useState({});         // { `${apt_id}-${ut_id}`: bool }
  const [saveErrors, setSaveErrors] = useState({});
  const [consumptionError, setConsumptionError] = useState('');

  useEffect(() => {
    authService.getUserInfo()
      .then(data => setUserInfo(data))
      .catch(() => setError(t('dashboard.failedUserInfo')));
  }, [t]);

  // ── Chart helpers ──────────────────────────────────────────────────────────

  const buildChartData = useCallback((readings, raw = false) => {
    // Generate last 12 month keys (oldest → newest)
    const now = new Date();
    const months = Array.from({ length: 12 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (11 - i), 1);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    });
    // Extra month before the range to compute the first delta
    const firstD = new Date(now.getFullYear(), now.getMonth() - 12, 1);
    const prevMonthKey = `${firstD.getFullYear()}-${String(firstD.getMonth() + 1).padStart(2, '0')}`;
    const allMonths = [prevMonthKey, ...months];

    // Group readings by apartment (null → '__building__' for building-scope meters)
    const byApt = {};
    readings.forEach(r => {
      const k = r.apartment ?? '__building__';
      (byApt[k] = byApt[k] || []).push(r);
    });

    // Latest reading value per (apartment, month)
    const monthlyByApt = {};
    Object.entries(byApt).forEach(([apt, rs]) => {
      monthlyByApt[apt] = {};
      allMonths.forEach(m => {
        const inM = rs.filter(r => r.reading_date.substring(0, 7) === m);
        if (inM.length) {
          inM.sort((a, b) => new Date(b.reading_date) - new Date(a.reading_date));
          monthlyByApt[apt][m] = parseFloat(inM[0].value);
        }
      });
    });

    if (raw) {
      // Raw reading values — for single apartment view
      return months.map((m) => {
        let total = 0, hasData = false;
        Object.values(monthlyByApt).forEach(aptM => {
          if (aptM[m] !== undefined) {
            total += aptM[m];
            hasData = true;
          }
        });
        const [y, mo] = m.split('-');
        const label = new Date(parseInt(y), parseInt(mo) - 1, 1)
          .toLocaleDateString('et-EE', { month: 'short', year: '2-digit' });
        return { month: m, label, value: hasData ? Math.round(total * 1000) / 1000 : null };
      });
    }

    // Delta mode — consumption between consecutive months
    return months.map((m, idx) => {
      const prevM = allMonths[idx];
      let total = 0, hasData = false;
      Object.values(monthlyByApt).forEach(aptM => {
        const cur = aptM[m], prev = aptM[prevM];
        if (cur !== undefined && prev !== undefined && cur >= prev) {
          total += cur - prev;
          hasData = true;
        }
      });
      const [y, mo] = m.split('-');
      const label = new Date(parseInt(y), parseInt(mo) - 1, 1)
        .toLocaleDateString('et-EE', { month: 'short', year: '2-digit' });
      return { month: m, label, value: hasData ? Math.round(total * 1000) / 1000 : null };
    });
  }, []);

  useEffect(() => {
    if (!activeUnion) return;
    setChartsLoading(true);
    const startDate = (() => {
      const d = new Date();
      d.setMonth(d.getMonth() - 13);
      return d.toISOString().slice(0, 10);
    })();

    adminService.getUtilityTypes()
      .then(async (data) => {
        const types = (Array.isArray(data) ? data : data.results || [])
          .filter(u => u.calculation_method === 'BY_CONSUMPTION');
        setChartTypes(types);

        const entries = await Promise.all(types.map(async (u) => {
          try {
            const params = { utility_type: u.id, start_date: startDate };
            if (activeApartment) params.apartment = activeApartment.id;
            const rd = await adminService.getMeterReadings(params);
            const readings = Array.isArray(rd) ? rd : rd.results || [];
            return [u.id, buildChartData(readings, !!activeApartment)];
          } catch {
            return [u.id, []];
          }
        }));
        setChartData(Object.fromEntries(entries));
      })
      .catch(() => {}) // charts are optional — silent on error
      .finally(() => setChartsLoading(false));
  }, [activeUnion, activeApartment, buildChartData]);

  // Admin summary stats
  useEffect(() => {
    if (!activeUnion || !isAdminView) return;
    setAdminStatsLoading(true);
    const now = new Date();
    const thisYear = now.getFullYear();
    const thisMonth = now.getMonth() + 1;

    Promise.all([
      axios.get('/api/apartments/', { headers: authService.authHeader() }),
      axios.get('/api/sent-invoices/', {
        headers: authService.authHeader(),
        params: { year: thisYear, month: thisMonth },
      }),
    ]).then(([aptsRes, invoicesRes]) => {
      const apts = Array.isArray(aptsRes.data) ? aptsRes.data : aptsRes.data.results || [];
      const invoices = Array.isArray(invoicesRes.data) ? invoicesRes.data : invoicesRes.data.results || [];

      const totalApts = apts.length;
      const withOwners = apts.filter(a => (a.owner_count || 0) > 0).length;
      const withoutOwners = totalApts - withOwners;

      const sent = invoices.length;
      const paid = invoices.filter(i => i.paid_at).length;
      const pending = sent - paid;
      const outstanding = invoices
        .filter(i => !i.paid_at)
        .reduce((sum, i) => sum + parseFloat(i.total_amount || 0), 0);

      setAdminStats({ totalApts, withOwners, withoutOwners, sent, paid, pending, outstanding });
    }).catch(() => {
      // Silent — dashboard stats are non-critical
    }).finally(() => setAdminStatsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeUnion, isAdminView]);

  useEffect(() => {
    if (!activeUnion) return;
    setLoadingConsumption(true);
    setConsumptionError('');
    const consumptionParams = { year: THIS_YEAR, month: THIS_MONTH };
    if (activeApartment) consumptionParams.apartment = activeApartment.id;
    axios.get('/api/meter-readings/my-consumption/', {
      headers: authService.authHeader(),
      params: consumptionParams,
    })
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : [];
        setConsumptionData(data);
        // Pre-fill inputs with existing readings
        const initValues = {};
        data.forEach(apt => {
          apt.readings.forEach(r => {
            const key = `${apt.apartment_id}-${r.utility_type_id}`;
            if (r.current_reading) initValues[key] = r.current_reading.value;
          });
        });
        setValues(initValues);
      })
      .catch(err => {
        const status = err.response?.status;
        if (status && status !== 403 && status !== 404) {
          setConsumptionError(t('dashboard.failedMeterReadings', { status }));
        }
        // 403/404 = not an owner or no union — silent
      })
      .finally(() => setLoadingConsumption(false));
  }, [activeUnion, activeApartment, t]);

  const handleValueChange = (key, val) => {
    setValues(v => ({ ...v, [key]: val }));
    setSaved(s => ({ ...s, [key]: false }));
  };

  const handleSubmitReading = async (aptId, utId, existingReadingId) => {
    const key = `${aptId}-${utId}`;
    const val = parseFloat(values[key]);
    if (isNaN(val) || val < 0) {
      setSaveErrors(e => ({ ...e, [key]: t('dashboard.invalidNumber') }));
      return;
    }
    setSaving(s => ({ ...s, [key]: true }));
    setSaveErrors(e => ({ ...e, [key]: null }));
    try {
      const today = new Date().toISOString().split('T')[0];
      if (existingReadingId) {
        await axios.patch(`/api/meter-readings/${existingReadingId}/`, { value: val }, {
          headers: authService.authHeader(),
        });
      } else {
        await axios.post('/api/meter-readings/', {
          apartment: aptId,
          utility_type: utId,
          value: val,
          reading_date: today,
        }, { headers: authService.authHeader() });
      }
      setSaved(s => ({ ...s, [key]: true }));
      // Refresh data
      const res = await axios.get('/api/meter-readings/my-consumption/', {
        headers: authService.authHeader(),
        params: { year: THIS_YEAR, month: THIS_MONTH },
      });
      const data = Array.isArray(res.data) ? res.data : [];
      setConsumptionData(data);
      const newValues = { ...values };
      data.forEach(apt => apt.readings.forEach(r => {
        const k = `${apt.apartment_id}-${r.utility_type_id}`;
        if (r.current_reading) newValues[k] = r.current_reading.value;
      }));
      setValues(newValues);
    } catch (err) {
      setSaveErrors(e => ({ ...e, [key]: t('dashboard.failedToSave') }));
    } finally {
      setSaving(s => ({ ...s, [key]: false }));
    }
  };

  const hasConsumption = consumptionData.length > 0 &&
    consumptionData.some(apt => apt.readings.length > 0);

  return (
    <Layout>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>{t('dashboard.title')}</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {userInfo && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {t('dashboard.loggedInAs')} <strong>{userInfo.username}</strong> ({userInfo.email})
        </Alert>
      )}

      {/* Admin summary card */}
      {isAdminView && (adminStatsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, mb: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : adminStats && (
        <Card sx={{ mb: 3 }}>
          <CardHeader title={t('dashboard.unionOverview')} subheader={`${MONTH_NAMES[THIS_MONTH]} ${THIS_YEAR}`} />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" color="primary">{adminStats.totalApts}</Typography>
                  <Typography variant="body2" color="text.secondary">{t('dashboard.totalApartments')}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" color="success.main">{adminStats.withOwners}</Typography>
                  <Typography variant="body2" color="text.secondary">{t('dashboard.withOwners')}</Typography>
                  <Typography variant="caption" color="text.disabled">{adminStats.withoutOwners} {t('dashboard.without')}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" color="primary">{adminStats.sent}</Typography>
                  <Typography variant="body2" color="text.secondary">{t('dashboard.invoicesSent')}</Typography>
                  <Typography variant="caption" color="text.disabled">{adminStats.paid} {t('dashboard.paid')}, {adminStats.pending} {t('dashboard.pending')}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" color="warning.main">{adminStats.outstanding.toFixed(0)}&euro;</Typography>
                  <Typography variant="body2" color="text.secondary">{t('dashboard.outstanding')}</Typography>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ))}

      {/* Meter readings card — only shown to apt owners with consumption utilities */}
      {consumptionError && (
        <Alert severity="error" sx={{ mb: 2 }}>{consumptionError}</Alert>
      )}

      {loadingConsumption && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {/* Consumption charts */}
      {(chartsLoading || chartTypes.length > 0) && (
        <Card sx={{ mb: 3 }}>
          <CardHeader
            avatar={<ShowChartIcon color="primary" />}
            title={activeApartment ? t('dashboard.meterReadingsLast12') : t('dashboard.consumptionLast12')}
            subheader={activeApartment ? t('dashboard.apartment', { number: activeApartment.unit_number }) : t('dashboard.monthlyTotals')}
          />
          <CardContent>
            {chartsLoading ? (
              <Grid container spacing={3}>
                {[0, 1].map(i => (
                  <Grid item xs={12} sm={6} key={i}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Skeleton variant="text" width="40%" height={24} sx={{ mb: 0.5 }} />
                      <Skeleton variant="text" width="25%" height={16} sx={{ mb: 1.5 }} />
                      <Skeleton variant="rectangular" width="100%" height={140} sx={{ borderRadius: 1 }} />
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Grid container spacing={3}>
                {chartTypes.map(u => {
                  const points = chartData[u.id] || [];
                  const hasAnyData = points.some(p => p.value !== null);
                  return (
                    <Grid item xs={12} sm={6} key={u.id}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {u.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                          {u.meter_scope === 'BUILDING' ? t('dashboard.wholeBuilding') : t('dashboard.allApartments')}
                          {u.unit ? ` · ${u.unit}` : ''}
                        </Typography>
                        {!hasAnyData ? (
                          <Box sx={{ height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography variant="body2" color="text.disabled">{t('dashboard.noReadings')}</Typography>
                          </Box>
                        ) : (
                          <ResponsiveContainer width="100%" height={140}>
                            <AreaChart data={points} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                              <defs>
                                <linearGradient id={`grad-${u.id}`} x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#2563EB" stopOpacity={0.25} />
                                  <stop offset="95%" stopColor="#2563EB" stopOpacity={0} />
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                              <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                              <YAxis
                                tick={{ fontSize: 11 }}
                                tickLine={false}
                                axisLine={false}
                                width={48}
                                tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}
                              />
                              <Tooltip
                                formatter={v => [
                                  v !== null ? `${v}${u.unit ? ' ' + u.unit : ''}` : '—',
                                  activeApartment ? t('dashboard.reading', { unit: '' }).replace(/ *\(.*\)/, '') : t('dashboard.consumption'),
                                ]}
                                contentStyle={{ fontSize: 12 }}
                              />
                              <Area
                                type="monotone"
                                dataKey="value"
                                stroke="#2563EB"
                                strokeWidth={2}
                                fill={`url(#grad-${u.id})`}
                                connectNulls={false}
                                dot={{ r: 3, fill: '#2563EB', strokeWidth: 0 }}
                                activeDot={{ r: 5 }}
                              />
                            </AreaChart>
                          </ResponsiveContainer>
                        )}
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </CardContent>
        </Card>
      )}

      {!loadingConsumption && hasConsumption && (
        <Card sx={{ mb: 3 }}>
          <CardHeader
            avatar={<SpeedIcon color="primary" />}
            title={t('dashboard.meterReadingsMonth', { month: MONTH_NAMES[THIS_MONTH], year: THIS_YEAR })}
            subheader={t('dashboard.meterReadingsSubheader')}
          />
          <CardContent>
            {consumptionData.map((apt) => (
              <Box key={apt.apartment_id} sx={{ mb: apt.readings.length > 1 ? 2 : 0 }}>
                {consumptionData.length > 1 && (
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('dashboard.apartment', { number: apt.apartment_number })}
                  </Typography>
                )}
                <Grid container spacing={2}>
                  {apt.readings.map((r) => {
                    const key = `${apt.apartment_id}-${r.utility_type_id}`;
                    const isSaving = saving[key];
                    const isSaved = saved[key];
                    const saveErr = saveErrors[key];
                    const prevLabel = r.previous_reading
                      ? t('dashboard.previousReading', { value: r.previous_reading.value, unit: r.unit, date: r.previous_reading.reading_date })
                      : r.default_start_value
                        ? t('dashboard.defaultStart', { value: r.default_start_value, unit: r.unit })
                        : t('dashboard.noPreviousReading');

                    return (
                      <Grid item xs={12} sm={6} md={4} key={r.utility_type_id}>
                        <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="subtitle2">{r.utility_name}</Typography>
                            {r.current_reading && (
                              <Chip icon={<CheckCircleIcon />} label="Submitted" size="small" color="success" variant="outlined" />
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                            {prevLabel}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                            <TextField
                              size="small"
                              label={t('dashboard.reading', { unit: r.unit || 'units' })}
                              type="number"
                              inputProps={{ step: '0.01', min: 0 }}
                              value={values[key] ?? ''}
                              onChange={e => handleValueChange(key, e.target.value)}
                              error={!!saveErr}
                              helperText={saveErr || ''}
                              sx={{ flex: 1 }}
                            />
                            <Button
                              variant={r.current_reading ? 'outlined' : 'contained'}
                              size="small"
                              onClick={() => handleSubmitReading(
                                apt.apartment_id, r.utility_type_id,
                                r.current_reading?.id || null
                              )}
                              disabled={isSaving || values[key] === undefined || values[key] === ''}
                              sx={{ mt: '4px', minWidth: 70 }}
                            >
                              {isSaving ? <CircularProgress size={16} /> : isSaved ? t('dashboard.saved') : r.current_reading ? t('dashboard.update') : t('dashboard.submit')}
                            </Button>
                          </Box>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
                {consumptionData.indexOf(apt) < consumptionData.length - 1 && <Divider sx={{ mt: 2 }} />}
              </Box>
            ))}
          </CardContent>
        </Card>
      )}
    </Layout>
  );
}

export default Dashboard;
